import * as React from 'react';
import { SketchPicker } from 'react-color';
import { useOnClickOutside } from '../../../hooks/useOnClickOutside';
import { useCallback, useEffect, useRef, useState } from 'react';
function addAlpha(color, opacity) {
    var _opacity = Math.round(Math.min(Math.max(opacity || 1, 0), 1) * 255);
    return color + _opacity.toString(16).toUpperCase();
}
function extractRgb(css) {
    return css.match(/[0-9.]+/gi);
}
function isRGBA(color) {
    return color.indexOf('rgb') > -1;
}
function rgbaToHex(r, g, b, a) {
    var outParts = [
        r.toString(16),
        g.toString(16),
        b.toString(16),
        Math.round(a * 255).toString(16).substring(0, 2)
    ];

    outParts.forEach(function (part, i) {
        if (part.length === 1) {
            outParts[i] = '0' + part;
        }
    });

    return ('#' + outParts.join(''));
}

export enum ColorPickerVerticalPosition {
    Bottom = 'bottom',
    Top = 'top',
    Smart = 'smart',
}

export enum ColorPickerHorizontalPosition {
    Left = 'left',
    Right = 'right',
    Center = 'center',
}

type ColorPickerProps = {
    color: string
    onChange: (color) => void
    onClose: () => void
    verticalPosition?: ColorPickerVerticalPosition
    horizontalPosition?: ColorPickerHorizontalPosition
    disableAlpha?: boolean;
    containerRef?: {
        current: HTMLDivElement
    };
    isHex?: boolean;
    width?: string;
    height?: string;
}

export function ColorPicker({
    disableAlpha,
    color,
    verticalPosition = ColorPickerVerticalPosition.Bottom,
    horizontalPosition = ColorPickerHorizontalPosition.Center,
    onChange,
    onClose,
    containerRef,
    isHex,
    width,
    height
}: ColorPickerProps) {

    const [opened, setOpened] = useState(false);
    const [prefferrableVerticalPosition, setPrefferrableVerticalPosition] = useState(null);

    const ref = useRef<HTMLDivElement>();

    const getPrefferrableVerticalPosition = useCallback(() => {
        const el = ref.current;
        const cpHeight = 304;
        if (el && containerRef) {
            const container = containerRef.current;
            const elemTop = el.offsetTop - container.offsetTop;

            if (elemTop - container.scrollTop + cpHeight <= container.clientHeight) {
                return ColorPickerVerticalPosition.Bottom;
            }

            return ColorPickerVerticalPosition.Top;
        }
        return ColorPickerVerticalPosition.Bottom;
    }, [containerRef]);

    useOnClickOutside(ref, useCallback(() => {
        if (opened) {
            setOpened(false);
            onClose();
        }
    }, [onClose, opened]));

    const openColorPickerCb = useCallback(() => {
        if (verticalPosition === ColorPickerVerticalPosition.Smart) {
            setPrefferrableVerticalPosition(getPrefferrableVerticalPosition());
        }
        setOpened(true);
    }, [getPrefferrableVerticalPosition, verticalPosition]);

    const listenEnterEventCb = useCallback((e) => {
        if (e.keyCode === 13) {
            setOpened(false);
            onClose();
        }
    }, []);

    useEffect(() => {
        const inputs = ref?.current?.querySelectorAll('input');

        inputs?.forEach((input) => {
            input.addEventListener('keyup', listenEnterEventCb);
        });

        return () => {
            inputs?.forEach((input) => {
                input.removeEventListener('keyup', listenEnterEventCb);
            });
        };
    }, [opened, listenEnterEventCb]);

    const onColorChangeCb = useCallback((color, event) => {
        const { rgb, hex } = color;
        if (isHex) {
            onChange(hex !== 'transparent' ? addAlpha(hex, +rgb.a) : hex);
        } else {
            onChange(`rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${rgb.a})`);
        }
    }, [isHex, onChange]);

    const [r, g, b, a] = isRGBA(color) ? extractRgb(color) : [0, 0, 0, 0];
    const style = {
        backgroundColor: color
    } as any;

    if (width) {
        style.width = width;
    }
    if (height) {
        style.height = height;
        style.minHeight = height;
        style.maxHeight = height;
    }

    return <div className={`color-picker-area cp-${prefferrableVerticalPosition || verticalPosition} cp-${horizontalPosition}`} ref={ref}>
        <div
            className="color-picker"
            style={style}
            onClick={openColorPickerCb}>
            <div className="color-picker_color-name">
                {isHex && isRGBA(color) ? rgbaToHex(+r, +g, +b, +a) : color}
            </div>

        </div>
        {opened &&
            <SketchPicker
                color={color}
                disableAlpha={disableAlpha}
                onChange={onColorChangeCb} />}
    </div>;

}